import { useCallback, useEffect, useState } from "react";
import { ApiError, api } from "./api";
import { Landing } from "./components/Landing";
import Dashboard from "./components/Dashboard";
import { ConnectSources } from "./components/ConnectSources";
import KonamiEgg from "./components/KonamiEgg";
import { useUI } from "./lib/theme";
import { AuthModal } from "./components/AuthModal";
import type { DashboardData } from "./lib/types";
import { DEMO_DATA } from "./lib/demoData";

type State =
  | { status: "loading" }
  | { status: "out" }
  | { status: "blocked" }
  | { status: "demo" }
  | { status: "in_no_data" }
  | { status: "in"; data: DashboardData };

/** Pantalla para un usuario con el acceso pausado por el admin (403 acceso_pausado). */
function BlockedScreen({ onLogout }: { onLogout: () => void }) {
  const { t } = useUI();
  return (
    <main className="flex min-h-[100dvh] flex-col items-center justify-center gap-4 px-4 text-center">
      <span aria-hidden className="text-4xl">
        ⏸️
      </span>
      <h1 className="text-2xl font-extrabold tracking-tight">{t("blocked_title")}</h1>
      <p className="max-w-sm text-sm text-muted">{t("blocked_desc")}</p>
      <button
        onClick={onLogout}
        className="rounded-xl border border-line px-4 py-2 text-sm font-medium text-muted transition hover:text-fg"
      >
        {t("blocked_logout")}
      </button>
    </main>
  );
}

export function App() {
  const { t } = useUI();
  const [state, setState] = useState<State>({ status: "loading" });
  const [demoAuthOpen, setDemoAuthOpen] = useState(false);

  const enterDemo = useCallback(() => {
    setState({ status: "demo" });
    const url = new URL(window.location.href);
    url.searchParams.set("demo", "1");
    window.history.replaceState(null, "", url.toString());
  }, []);

  const exitDemo = useCallback(() => {
    const url = new URL(window.location.href);
    url.searchParams.delete("demo");
    window.history.replaceState(null, "", url.toString());
    setState({ status: "out" });
  }, []);

  const load = useCallback(async (opts?: { forceIn?: boolean }) => {
    try {
      const me = await api.me();
      const skipped = sessionStorage.getItem("westive_skip_onboarding") === "1";
      if (!me.hasConnections && !skipped && !opts?.forceIn) {
        setState({ status: "in_no_data" });
        return;
      }
      const data = await api.dashboard();
      setState({ status: "in", data });
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) setState({ status: "blocked" });
      else setState({ status: "out" });
    }
  }, []);

  useEffect(() => {
    // Si la URL tiene ?demo=1, entrar en modo demo directamente.
    if (new URLSearchParams(window.location.search).get("demo") === "1") {
      setState({ status: "demo" });
      return;
    }
    void load({});
  }, [load]);

  const onLogout = useCallback(async () => {
    await api.logout();
    location.href = "/";
  }, []);

  let content;
  if (state.status === "loading") {
    content = (
      <main className="flex min-h-[100dvh] items-center justify-center">
        <p className="text-muted">Cargando…</p>
      </main>
    );
  } else if (state.status === "out") {
    content = (
      <Landing
        onAuthSuccess={() => void load({})}
        onNeedsConnection={() => setState({ status: "in_no_data" })}
        onDemo={enterDemo}
      />
    );
  } else if (state.status === "demo") {
    content = (
      <>
        {/* Banner de demo fijo */}
        <div className="fixed top-0 left-0 right-0 z-[100] flex items-center justify-between gap-3 bg-accent px-4 py-2 text-sm font-semibold text-white shadow">
          <span>{t("demo_badge")}</span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setDemoAuthOpen(true)}
              className="rounded-lg border border-white/40 px-3 py-1 text-xs font-semibold transition hover:bg-white/20"
            >
              {t("demo_cta_signup")}
            </button>
            <button
              onClick={exitDemo}
              className="rounded-lg border border-white/40 px-3 py-1 text-xs font-medium transition hover:bg-white/20"
            >
              {t("demo_exit")}
            </button>
          </div>
        </div>
        {/* Dashboard con datos demo, sin mutaciones */}
        <div className="pt-[40px]">
          <Dashboard data={DEMO_DATA} onLogout={exitDemo} onReload={() => {}} demo />
        </div>
        <AuthModal
          open={demoAuthOpen}
          onClose={() => setDemoAuthOpen(false)}
          onSuccess={() => { setDemoAuthOpen(false); void load({}); }}
          onNeedsConnection={() => { setDemoAuthOpen(false); setState({ status: "in_no_data" }); }}
          defaultTab="register"
        />
      </>
    );
  } else if (state.status === "blocked") {
    content = <BlockedScreen onLogout={onLogout} />;
  } else if (state.status === "in_no_data") {
    content = (
      <ConnectSources
        onConnected={() => void load()}
        onSkip={() => {
          sessionStorage.setItem("westive_skip_onboarding", "1");
          void load({ forceIn: true });
        }}
      />
    );
  } else {
    content = <Dashboard data={state.data} onLogout={onLogout} onReload={() => void load({})} />;
  }

  // El easter egg (código Konami) se monta global: funciona en landing y en el panel.
  return (
    <>
      {content}
      <KonamiEgg />
    </>
  );
}
